/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Wed Jan 31 2024
 **/

import { useEffect, useMemo, DependencyList } from "react";
import { atom, useAtom } from "jotai";
import { has, omit } from "lodash-es";
import { useEventHandlers } from "@rfice/eventhandler";
import { JotaiStore } from "@/frameworks/libraries/commons";

export type SubscribeParameter = { [key: string]: any };

type Responder<T> = Record<string, T>;

type SubscribeOptions<R, C> = {
	key: string;
	parameter?: SubscribeParameter;
	responder: R | null;
};

export const CommanderStorageAtom = atom<Record<string, any>>({});

export default function useCommandProducer<C, R>(
	name: string,
	commander: C,
	initializer: {
		onSubscribe?: (parameter: SubscribeParameter) => void;
		onUnsubscribe?: (parameter: SubscribeParameter) => void;
	} | null,
	deps: DependencyList = []
): {
	/**
	 * 등록된 Responder를 가져옵니다.
	 * @param key 등록된 Responder 키 (`subscribe()`의 첫번째 파라메터)
	 * @returns `R | null` - `useCommandProducer()`의 R 타입 리턴
	 */
	get: (key: string) => R | null;
	runAll: (fn: keyof R, ...value: any[]) => any[];
	command: C;
} {
	const [commanderStorage, setCommanderStorage] = useAtom(CommanderStorageAtom, { store: JotaiStore });
	const _commander = useMemo(() => ({ ...commander }), [...deps]);

	useEffect(() => {
		// console.debug("#useCommandProducer# update commander storage");
		setCommanderStorage((prev) => ({ ...prev, [name]: { ...prev[name], commander: _commander } }));
	}, [_commander]);

	const eventName = name.charAt(0).toUpperCase() + name.slice(1);
	useEventHandlers(
		{
			[`on${eventName}Subscribe`](parameter: SubscribeOptions<R, C>) {
				console.debug(`#useCommandProducer# subscribe with`, parameter);
				initializer?.onSubscribe?.(parameter);

				const { key, responder } = parameter;
				setCommanderStorage((prev) => ({
					...prev,
					[name]: { ...prev[name], responders: { ...prev[name].responders, [key]: responder } }
				}));
			},
			[`on${eventName}Unsubscribe`](parameter: SubscribeOptions<R, C>) {
				console.debug(`#useCommandProducer# unsubscribe with`, parameter);
				initializer?.onUnsubscribe?.(parameter);

				const { key } = parameter;
				setCommanderStorage((prev) => ({
					...prev,
					[name]: { ...prev[name], responders: omit(commanderStorage[name].responders, key) }
				}));
			}
		},
		[...deps, commanderStorage]
	);

	return {
		get(key: string) {
			if (has(commanderStorage, name) && has(commanderStorage[name].responders, key)) {
				return commanderStorage[name].responders[key] ?? null;
			}
			return null;
		},
		runAll(fn: keyof R, ...value: any[]) {
			return (
				(has(commanderStorage, name) &&
					commanderStorage[name].responders &&
					Object.keys(commanderStorage[name].responders).map((key) =>
						commanderStorage[name].responders[key][fn]?.(...value)
					)) ||
				[]
			);
		},
		get command() {
			return _commander;
		}
	};
}
